import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatUSD, formatLBP } from "@/lib/api";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight } from "lucide-react";
import LbpAmountInput from "@/components/ui/LbpAmountInput";

export default function OperationsPage() {
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("deposit");

  const refresh = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/users");
      setUsers(data);
    } catch {
      toast.error("تعذّر تحميل المستخدمين");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="space-y-6" data-testid="operations-page">
      <header>
        <div className="text-xs text-[var(--c-gold)] tracking-[0.3em] uppercase font-semibold">صلاحيات المدير</div>
        <h1 className="section-title text-3xl sm:text-4xl mt-2">العمليات المالية</h1>
        <p className="text-[var(--c-muted)] mt-2 text-sm">إيداع وسحب وتحويل بين المستخدمين بالدولار أو الليرة.</p>
      </header>

      <div className="flex flex-wrap gap-2 bg-white rounded-2xl p-2 border border-[var(--c-border)] w-fit">
        <TabBtn active={tab === "deposit"} onClick={() => setTab("deposit")} icon={ArrowDownToLine} testid="tab-deposit">إيداع</TabBtn>
        <TabBtn active={tab === "withdraw"} onClick={() => setTab("withdraw")} icon={ArrowUpFromLine} testid="tab-withdraw">سحب</TabBtn>
        <TabBtn active={tab === "transfer"} onClick={() => setTab("transfer")} icon={ArrowLeftRight} testid="tab-transfer">تحويل</TabBtn>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <div className="bg-white border border-[var(--c-border)] rounded-2xl p-6 sm:p-8">
          {tab === "deposit" && <SingleOpForm users={users} kind="deposit" onDone={refresh} />}
          {tab === "withdraw" && <SingleOpForm users={users} kind="withdraw" onDone={refresh} />}
          {tab === "transfer" && <TransferForm users={users} onDone={refresh} />}
        </div>

        <div className="bg-white border border-[var(--c-border)] rounded-2xl p-6 sm:p-8">
          <h3 className="section-title text-lg mb-4">أرصدة سريعة</h3>
          {users.length === 0 ? (
            <div className="text-sm text-[var(--c-muted)]">لا يوجد مستخدمون. أضف مستخدماً من قسم «المستخدمون».</div>
          ) : (
            <ul className="divide-y divide-[var(--c-border)] max-h-[460px] overflow-auto">
              {users.map((u) => (
                <li key={u.id} className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-bold text-[var(--c-navy)]">{u.display_name}</div>
                    <div className="text-xs text-[var(--c-muted)]">@{u.username}</div>
                  </div>
                  <div className="text-end">
                    <div className="font-num text-sm text-[var(--c-gold)] font-bold">$ {formatUSD(u.balance_usd)}</div>
                    <div className="font-num text-xs text-[var(--c-navy)]">{formatLBP(u.balance_lbp)} ل.ل</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function TabBtn({ children, active, onClick, icon: Icon, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all
        ${active ? "bg-[var(--c-navy)] text-[var(--c-gold-light)] shadow-md" : "text-[var(--c-navy)]/70 hover:text-[var(--c-navy)] hover:bg-[var(--c-cream)]"}`}>
      <Icon className="w-4 h-4" />{children}
    </button>
  );
}

function SingleOpForm({ users, kind, onDone }) {
  const [userId, setUserId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const title = kind === "deposit" ? "إيداع" : "سحب";

  const submit = async (e) => {
    e.preventDefault();
    if (!userId) return toast.error("اختر المستخدم");
    if (!amount || Number(amount) <= 0) return toast.error("أدخل مبلغاً صحيحاً");
    setLoading(true);
    const finalAmount = currency === "LBP" ? Number(amount) * 1000 : Number(amount);
    try {
      await adminApi.post(`/admin/operations/${kind}`, {
        user_id: userId, currency, amount: finalAmount, note,
      });
      toast.success(`${title} ناجح`);
      setAmount(""); setNote("");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `فشل ${title}`);
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`form-${kind}`}>
      <h3 className="section-title text-lg">{title} للحساب</h3>
      <div><label className="label-pro">المستخدم</label>
        <select className="input-pro" value={userId} onChange={(e) => setUserId(e.target.value)} data-testid={`${kind}-user`}>
          <option value="">— اختر المستخدم —</option>
          {users.map((u) => <option key={u.id} value={u.id}>{`${u.display_name} — @${u.username}`}</option>)}
        </select></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="label-pro">العملة</label>
          <select className="input-pro" value={currency} onChange={(e) => { setCurrency(e.target.value); setAmount(""); }} data-testid={`${kind}-currency`}>
            <option value="USD">دولار أمريكي ($)</option>
            <option value="LBP">ليرة لبنانية (ل.ل)</option>
          </select></div>
        <div><label className="label-pro">المبلغ {currency === "LBP" ? "(بالآلاف)" : ""}</label>
          <LbpAmountInput currency={currency} value={amount} onChange={setAmount} placeholder="0" testid={`${kind}-amount`} /></div>
      </div>
      <div><label className="label-pro">ملاحظة (اختياري)</label>
        <input className="input-pro" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`${kind}-note`} /></div>
      <button disabled={loading} className="btn-navy w-full rounded-xl py-3 font-bold" data-testid={`${kind}-submit`}>
        {loading ? "جارٍ…" : `تأكيد ${title}`}
      </button>
    </form>
  );
}

function TransferForm({ users, onDone }) {
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!fromId || !toId) return toast.error("اختر الطرفين");
    if (fromId === toId) return toast.error("لا يمكن التحويل لنفس المستخدم");
    if (!amount || Number(amount) <= 0) return toast.error("أدخل مبلغاً صحيحاً");
    setLoading(true);
    const finalAmount = currency === "LBP" ? Number(amount) * 1000 : Number(amount);
    try {
      await adminApi.post("/admin/operations/transfer", {
        from_user_id: fromId, to_user_id: toId, currency, amount: finalAmount, note,
      });
      toast.success("تم التحويل");
      setAmount(""); setNote("");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل التحويل");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="form-transfer">
      <h3 className="section-title text-lg">تحويل بين المستخدمين</h3>
      <div className="grid grid-cols-1 gap-3">
        <div><label className="label-pro">من</label>
          <select className="input-pro" value={fromId} onChange={(e) => setFromId(e.target.value)} data-testid="transfer-from">
            <option value="">— المرسل —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{`${u.display_name} — @${u.username}`}</option>)}
          </select></div>
        <div><label className="label-pro">إلى</label>
          <select className="input-pro" value={toId} onChange={(e) => setToId(e.target.value)} data-testid="transfer-to">
            <option value="">— المستلم —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{`${u.display_name} — @${u.username}`}</option>)}
          </select></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="label-pro">العملة</label>
          <select className="input-pro" value={currency} onChange={(e) => { setCurrency(e.target.value); setAmount(""); }} data-testid="transfer-currency">
            <option value="USD">دولار أمريكي ($)</option>
            <option value="LBP">ليرة لبنانية (ل.ل)</option>
          </select></div>
        <div><label className="label-pro">المبلغ {currency === "LBP" ? "(بالآلاف)" : ""}</label>
          <LbpAmountInput currency={currency} value={amount} onChange={setAmount} placeholder="0" testid="transfer-amount" /></div>
      </div>
      <div><label className="label-pro">ملاحظة (اختياري)</label>
        <input className="input-pro" value={note} onChange={(e) => setNote(e.target.value)} data-testid="transfer-note" /></div>
      <button disabled={loading} className="btn-navy w-full rounded-xl py-3 font-bold" data-testid="transfer-submit">
        {loading ? "جارٍ…" : "تنفيذ التحويل"}
      </button>
    </form>
  );
}
